/**
 * Single source for “what downstream Nodon modelling still cares about” vs noise.
 * Intended for extractor and merger prompts only — never copy verbatim into
 * stored consolidation descriptions unless it is rewritten as human prose.
 *
 * Mirrors the design tool surface roughly (massing, storey stack, coarse area/site),
 * without naming internal tool identifiers.
 */

const PRIORITIZE_BLOCK = [
  '## Prefer evidence for (when the drawing/visual shows it)',
  '- **Envelope & footprint morphology**: elongated bar/slab, perimeter block, L/U/T/stepped footprints, perimeter courtyard/light-well, fragmented volumes, tower vs podium — words the document supports.',
  '- **Rough scale**: legible dimensions, storey count, parapet/roof cues, setbacks, segment offsets between wings when visible.',
  '- **Areas / stacking**: storey or total area callouts where printed; rough order-of-magnitude is enough — never fabricate decimals.',
  '- **Courtyards / cut-outs / shafts** affecting outline when depicted as voids.',
  '- **Stable identity cues**: stamping names/labels tied to masses (align with classifier hints when supplied).',
  '- **Readable height / storey rhythm** from elevations/sections rendered in-frame.',
];

const SKIP_BLOCK = [
  '## Spend little or no extraction effort on',
  '- **Room fit-out**, loose furniture counts, sanitary fixture schedules, acoustic tiles, unless they are the clearest cue for storey height/stacking affecting the shell.',
  '- **Legalese, tender boilerplate**, sustainability narrative fluff not tied to described geometry.',
  '- **Tiny annotation clutter** unrelated to perimeter or vertical build-up.',
];

const RENDERINGS_BLOCK = [
  '## Renditions / CGI / photos-only segments',
  '- Capture **massing silhouette** (tower vs slab vs staggered wings), façade grid if obvious, readable roof/form language, setbacks from grade.',
  '- Treat renderings as **lower dimensional fidelity than measured drawings** — prefer cautious language when depth is inferred from one viewpoint.',
];

/**
 * Concatenated Markdown-style guidance for prompting extractors / merger models.
 */
export const designExtractionProductScopePromptMarkdown = (): string =>
  [...PRIORITIZE_BLOCK, '', ...SKIP_BLOCK, '', ...RENDERINGS_BLOCK].join('\n');
